import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useOutletContext } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { requestInstitutionDonorsExportEmail } from '@/lib/institution-donors-export'
import {
  fetchInstitutionDonorDetail,
  fetchInstitutionDonors,
} from '@/lib/institution-donors'
import { formatCurrency, formatDateTime } from '@/lib/campaign-dashboard'
import { buildWhatsAppChatHref } from '@/lib/whatsapp-link'
import type { ClientesLayoutOutletContext } from '@/layouts/ClientesTabsLayout/ClientesTabsLayout'
import type {
  FetchInstitutionDonorsOptions,
  InstitutionDonorListItem,
  InstitutionDonorsPagination,
} from '@/types/institution-donor'
import styles from './ClientesListaPage.module.css'

const PER_PAGE = 15
const SEARCH_INPUT_ID = 'clientes-busca-nome'

export function ClientesListaPage() {
  const { campaignId, campaignsLoading, hasCampaigns, globalAdministration } =
    useOutletContext<ClientesLayoutOutletContext>()

  if (!globalAdministration && (campaignsLoading || campaignId === null)) {
    return (
      <div className={styles.centered}>
        {campaignsLoading || hasCampaigns ? (
          <>
            <Icon icon="mdi:loading" className={styles.spinner} />
            Carregando clientes…
          </>
        ) : (
          'Selecione uma campanha para visualizar os clientes.'
        )}
      </div>
    )
  }

  return (
    <ClientesListaContent
      key={globalAdministration ? 'global' : campaignId}
      campaignId={globalAdministration ? null : campaignId}
    />
  )
}

function ClientesListaContent({ campaignId }: { campaignId: number | null }) {
  const [rows, setRows] = useState<InstitutionDonorListItem[]>([])
  const [pagination, setPagination] = useState<InstitutionDonorsPagination | null>(null)
  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [purchaseStatus, setPurchaseStatus] = useState<
    'all' | 'with_purchases' | 'without_purchases'
  >('all')
  const [minSpent, setMinSpent] = useState('')
  const [maxSpent, setMaxSpent] = useState('')
  const [sort, setSort] = useState<'recent' | 'highest_spend' | 'most_orders' | 'name'>(
    'recent'
  )
  const [appliedFilters, setAppliedFilters] = useState<FetchInstitutionDonorsOptions>({})
  const [filterError, setFilterError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)
  const [exportSuccess, setExportSuccess] = useState(false)
  const [contactError, setContactError] = useState<string | null>(null)
  const [openingWhatsAppId, setOpeningWhatsAppId] = useState<number | null>(null)
  const latestRequestId = useRef(0)

  useEffect(() => {
    const id = window.setTimeout(() => {
      setPage(1)
      setDebouncedSearch(searchInput.trim())
    }, 400)
    return () => window.clearTimeout(id)
  }, [searchInput])

  const load = useCallback(async () => {
    const requestId = ++latestRequestId.current
    setIsLoading(true)
    setError(null)
    try {
      const res = await fetchInstitutionDonors(debouncedSearch, {
        ...appliedFilters,
        campaign_id: campaignId ?? undefined,
        page,
        per_page: PER_PAGE,
      })
      if (requestId !== latestRequestId.current) return
      setRows(res.donors)
      setPagination(res.pagination)
    } catch {
      if (requestId !== latestRequestId.current) return
      setError('Erro ao carregar clientes.')
      setRows([])
      setPagination(null)
    } finally {
      if (requestId === latestRequestId.current) setIsLoading(false)
    }
  }, [appliedFilters, campaignId, debouncedSearch, page])

  useEffect(() => {
    void load()
    return () => {
      latestRequestId.current += 1
    }
  }, [load])

  const handleExport = useCallback(async () => {
    setExporting(true)
    setExportError(null)
    setExportSuccess(false)
    try {
      await requestInstitutionDonorsExportEmail(debouncedSearch, {
        ...appliedFilters,
        campaign_id: campaignId ?? undefined,
      })
      setExportSuccess(true)
    } catch (cause) {
      setExportError(
        cause instanceof Error
          ? cause.message
          : 'Não foi possível solicitar a exportação.'
      )
    } finally {
      setExporting(false)
    }
  }, [appliedFilters, campaignId, debouncedSearch])

  const applyFilters = () => {
    const minimum = minSpent.trim() === '' ? undefined : Number(minSpent)
    const maximum = maxSpent.trim() === '' ? undefined : Number(maxSpent)
    if (
      (minimum !== undefined && (!Number.isFinite(minimum) || minimum < 0)) ||
      (maximum !== undefined && (!Number.isFinite(maximum) || maximum < 0)) ||
      (minimum !== undefined && maximum !== undefined && minimum > maximum)
    ) {
      setFilterError(
        'Confira a faixa de valor gasto: o mínimo deve ser menor ou igual ao máximo.'
      )
      return
    }
    setFilterError(null)
    setPage(1)
    setAppliedFilters({
      purchase_status: purchaseStatus,
      min_spent: minimum,
      max_spent: maximum,
      sort,
    })
  }

  const clearFilters = () => {
    setSearchInput('')
    setDebouncedSearch('')
    setPurchaseStatus('all')
    setMinSpent('')
    setMaxSpent('')
    setSort('recent')
    setAppliedFilters({})
    setFilterError(null)
    setPage(1)
  }

  const hasActiveFilters = Boolean(
    debouncedSearch ||
    (appliedFilters.purchase_status && appliedFilters.purchase_status !== 'all') ||
    appliedFilters.min_spent !== undefined ||
    appliedFilters.max_spent !== undefined ||
    (appliedFilters.sort && appliedFilters.sort !== 'recent')
  )

  const handleWhatsApp = useCallback(async (donorId: number) => {
    setContactError(null)
    setOpeningWhatsAppId(donorId)
    const tab = window.open('about:blank', '_blank')
    if (tab) tab.opener = null

    try {
      const donor = await fetchInstitutionDonorDetail(donorId)
      const href = donor.phone ? buildWhatsAppChatHref(donor.phone) : null
      if (!href) throw new Error('WhatsApp indisponível para este cliente.')

      if (tab) {
        tab.location.replace(href)
      } else {
        window.location.assign(href)
      }
    } catch {
      tab?.close()
      setContactError('Não foi possível abrir o WhatsApp deste cliente. Tente novamente.')
    } finally {
      setOpeningWhatsAppId(null)
    }
  }, [])

  const showFullSpinner = isLoading && rows.length === 0

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <div className={styles.searchWrap}>
          <label className={styles.searchLabel} htmlFor={SEARCH_INPUT_ID}>
            Buscar cliente
          </label>
          <div className={styles.searchField}>
            <Icon icon="mdi:magnify" className={styles.searchIcon} aria-hidden />
            <input
              id={SEARCH_INPUT_ID}
              type="search"
              className={styles.searchInput}
              placeholder="Nome, e-mail, telefone, CPF/CNPJ ou ID"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              autoComplete="off"
            />
          </div>
        </div>

        <button
          type="button"
          className={styles.btnExport}
          onClick={() => void handleExport()}
          disabled={exporting || isLoading}
        >
          <Icon
            icon={exporting ? 'mdi:loading' : 'mdi:email-send-outline'}
            className={exporting ? styles.exportSpinner : undefined}
            aria-hidden
          />
          {exporting ? 'Solicitando envio…' : 'Exportar Clientes por e-mail'}
        </button>
      </div>

      <section className={styles.filtersPanel} aria-label="Filtros de clientes">
        <div className={styles.filtersIntro}>
          <div>
            <span className={styles.filtersEyebrow}>Refinar resultados</span>
            <h2>Filtros da lista</h2>
          </div>
          {hasActiveFilters ? (
            <span className={styles.activeBadge}>Filtros ativos</span>
          ) : null}
        </div>
        <form
          onSubmit={(event) => {
            event.preventDefault()
            applyFilters()
          }}
        >
          <div className={styles.filtersGrid}>
            <label className={styles.filterField}>
              <span>Compras pagas</span>
              <select
                value={purchaseStatus}
                onChange={(event) =>
                  setPurchaseStatus(event.target.value as typeof purchaseStatus)
                }
              >
                <option value="all">Todos os clientes</option>
                <option value="with_purchases">Com compras</option>
                <option value="without_purchases">Sem compras</option>
              </select>
            </label>
            <label className={styles.filterField}>
              <span>Gasto mínimo (R$)</span>
              <input
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                placeholder="0,00"
                value={minSpent}
                onChange={(event) => setMinSpent(event.target.value)}
              />
            </label>
            <label className={styles.filterField}>
              <span>Gasto máximo (R$)</span>
              <input
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                placeholder="Sem limite"
                value={maxSpent}
                onChange={(event) => setMaxSpent(event.target.value)}
              />
            </label>
            <label className={styles.filterField}>
              <span>Ordenar por</span>
              <select
                value={sort}
                onChange={(event) => setSort(event.target.value as typeof sort)}
              >
                <option value="recent">Compra mais recente</option>
                <option value="highest_spend">Maior valor gasto</option>
                <option value="most_orders">Mais compras</option>
                <option value="name">Nome (A–Z)</option>
              </select>
            </label>
          </div>
          {filterError ? (
            <p className={styles.filterError} role="alert">
              {filterError}
            </p>
          ) : null}
          <div className={styles.filterActions}>
            <button
              type="button"
              className={styles.clearFilters}
              onClick={clearFilters}
              disabled={
                !hasActiveFilters &&
                !searchInput &&
                purchaseStatus === 'all' &&
                !minSpent &&
                !maxSpent &&
                sort === 'recent'
              }
            >
              Limpar filtros
            </button>
            <button type="submit" className={styles.applyFilters}>
              <Icon icon="mdi:filter-check-outline" aria-hidden /> Aplicar filtros
            </button>
          </div>
        </form>
      </section>

      {exportError ? (
        <p className={styles.exportError} role="alert">
          {exportError}
        </p>
      ) : null}
      {exportSuccess ? (
        <p className={styles.exportSuccess} role="status">
          Solicitação recebida. O arquivo será enviado ao e-mail cadastrado da
          instituição.
        </p>
      ) : null}
      {contactError ? (
        <p className={styles.exportError} role="alert">
          {contactError}
        </p>
      ) : null}

      {showFullSpinner ? (
        <div className={styles.centered}>
          <Icon icon="mdi:loading" className={styles.spinner} />
          Carregando clientes…
        </div>
      ) : error ? (
        <div className={styles.centered}>
          <Icon icon="mdi:alert-circle-outline" className={styles.errorIcon} />
          {error}
          <button type="button" className={styles.retryBtn} onClick={() => void load()}>
            Tentar novamente
          </button>
        </div>
      ) : rows.length === 0 ? (
        <div className={styles.centered}>
          <Icon icon="mdi:account-multiple-outline" className={styles.emptyIcon} />
          Nenhum cliente encontrado.
        </div>
      ) : (
        <div className={styles.tableCard}>
          <div
            className={styles.tableScroll}
            role="region"
            aria-label="Tabela de clientes"
            tabIndex={0}
          >
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>WhatsApp</th>
                  <th>Email</th>
                  <th>Compras</th>
                  <th>Total gasto</th>
                  <th>Números</th>
                  <th>Última compra</th>
                  <th className={styles.actionsHeader}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <div className={styles.nameCell}>
                        <span className={styles.avatar} aria-hidden>
                          <Icon icon="mdi:account-circle" />
                        </span>
                        <span className={styles.nameText}>{row.name}</span>
                      </div>
                    </td>
                    <td>
                      {row.phone ? (
                        <button
                          type="button"
                          className={styles.phoneWhatsAppLink}
                          onClick={() => void handleWhatsApp(row.id)}
                          disabled={openingWhatsAppId === row.id}
                          aria-label={`Abrir WhatsApp de ${row.name}`}
                        >
                          <Icon
                            icon={
                              openingWhatsAppId === row.id
                                ? 'mdi:loading'
                                : 'mdi:whatsapp'
                            }
                            aria-hidden
                          />
                          {row.phone}
                        </button>
                      ) : (
                        <span className={styles.mutedCell}>—</span>
                      )}
                    </td>
                    <td>
                      {row.email ? (
                        <span className={styles.emailCell}>{row.email}</span>
                      ) : (
                        <span className={styles.mutedCell}>—</span>
                      )}
                    </td>
                    <td className={styles.numCell}>
                      {row.purchase_stats.paid_orders_count}
                    </td>
                    <td className={styles.amountCell}>
                      {formatCurrency(row.purchase_stats.total_paid_amount)}
                    </td>
                    <td className={styles.numCell}>
                      {row.purchase_stats.total_paid_quantity}
                    </td>
                    <td>
                      {row.purchase_stats.last_paid_at ? (
                        <span className={styles.dateCell}>
                          {formatDateTime(row.purchase_stats.last_paid_at)}
                        </span>
                      ) : (
                        <span className={styles.mutedCell}>—</span>
                      )}
                    </td>
                    <td>
                      <div className={styles.actionsCell}>
                        <Link
                          to={`/clientes/${row.id}/editar`}
                          className={styles.actionBtn}
                          title="Editar"
                          aria-label={`Editar ${row.name}`}
                        >
                          <Icon icon="mdi:pencil" />
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination && pagination.total > 0 ? (
            <div className={styles.tableFooter}>
              <span className={styles.paginationMeta}>
                {pagination.total} {pagination.total === 1 ? 'cliente' : 'clientes'}
                {pagination.last_page > 1
                  ? ` · Página ${pagination.current_page} de ${pagination.last_page}`
                  : null}
              </span>
              <span className={styles.statsNote}>
                Totais incluem somente compras pagas.
              </span>
              {pagination.last_page > 1 ? (
                <div className={styles.pagination}>
                  <button
                    type="button"
                    className={styles.pageBtn}
                    disabled={page <= 1 || isLoading}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Anterior
                  </button>
                  <button
                    type="button"
                    className={styles.pageBtn}
                    disabled={page >= pagination.last_page || isLoading}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Próxima
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      )}
    </div>
  )
}
